import { defineStore } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import type { ConditionalEffect } from '../types/conditional-effect';
import type { ConditionalEffectPreview } from '../types/conditional-effect-preview';
import * as EditorConditionsHelper from '../helpers/editor-conditions.helper';
import type {
  ConditionalContentSectionSelection,
  ConditionalContentSelection,
} from '../types/selection-payload';
import type { CreateTemplateConditionalEffectPayload } from '../types/create-template-conditional-effect-payload';
import type { ConditionalEffectPatch } from '../types/conditional-effect-patch';
import { conditionsToConditionPatches, effectToEffectPatch } from '../helpers/update.helper';
import type { EffectInput, EffectPatch } from '../types/effect-input';
import { contentIdsWithLimitReached } from '../helpers/conditional-effects-limit.helper';

export const useConditionalLogicStore = defineStore('conditional-logic', () => {
  const preventCloseReason = ref<'cancel' | 'close' | null>(null);
  const conditionalEffects = ref<ConditionalEffect[]>([]);
  const conditionalEffectPreviews = ref(new Map<string, ConditionalEffectPreview>());
  const opened = ref(false);
  const selectedConditionalEffectId = ref<string | null>();
  const updatedConditionalEffect = ref<
    ConditionalEffectPatch | CreateTemplateConditionalEffectPayload
  >();
  const highlightModeSelection = ref<ConditionalContentSelection | null>(null);
  const unsavedChanges = ref(false);

  const listMode = computed(() => opened.value && selectedConditionalEffectId.value === undefined);
  const editMode = computed(() => opened.value && !!selectedConditionalEffectId.value);
  const createMode = computed(() => opened.value && selectedConditionalEffectId.value === null);
  const highlightMode = computed(() => !opened.value && !!highlightModeSelection.value);
  const selectMode = computed(
    () =>
      !opened.value &&
      !highlightMode.value &&
      (!!selectedConditionalEffectId.value || selectedConditionalEffectId.value === null)
  );
  const overlayMode = computed(() => highlightMode.value || selectMode.value);

  const selectedConditionalEffect = computed(() =>
    conditionalEffects.value.find(effect => effect.id === selectedConditionalEffectId.value)
  );

  watch(
    updatedConditionalEffect,
    () => {
      if (editMode.value || createMode.value) {
        unsavedChanges.value = true;
      }
    },
    { deep: true }
  );

  const close = () => {
    opened.value = false;
    selectedConditionalEffectId.value = undefined;
    updatedConditionalEffect.value = undefined;
    unsavedChanges.value = false;
    preventCloseReason.value = null;
  };

  const openEdit = async (id: string, templateId: string) => {
    opened.value = true;
    selectedConditionalEffectId.value = id;

    const effect = selectedConditionalEffect.value;
    if (!effect) return;

    updatedConditionalEffect.value = {
      templateId,
      id: effect.id,
      name: effect.name,
      description: effect.description,
      truthyEffects: effect.truthyEffects.map(effectToEffectPatch),
      falsyEffects: effect.falsyEffects.map(effectToEffectPatch),
      conditionGroups: effect.conditionGroups.map(group => ({
        ...group,
        conditions: conditionsToConditionPatches(group.conditions),
      })),
    };
    await nextTick();
    unsavedChanges.value = false;
  };
  const openCreate = async (templateId: string) => {
    opened.value = true;
    selectedConditionalEffectId.value = null;
    updatedConditionalEffect.value = EditorConditionsHelper.createEmptyConditionalEffect({
      templateId,
    });
    await nextTick();
    unsavedChanges.value = false;
  };
  const openList = (templateId: string) => {
    if (!conditionalEffects.value.length) {
      openCreate(templateId);
      return;
    }
    opened.value = true;
    selectedConditionalEffectId.value = undefined;
    unsavedChanges.value = false;
  };

  const handleSelectModeKeyup = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      deactivateSelectMode();
      deactivateHighlightMode();
    }
  };

  const activateSelectMode = () => {
    opened.value = false;
    document.addEventListener('keyup', handleSelectModeKeyup, true);
  };

  const deactivateSelectMode = () => {
    opened.value = true;
    document.removeEventListener('keyup', handleSelectModeKeyup, true);
  };

  const activateHighlightMode = (effect: EffectInput | EffectPatch) => {
    opened.value = false;
    highlightModeSelection.value = EditorConditionsHelper.createSelectionFromEditorEffect(effect);
    document.addEventListener('keyup', handleSelectModeKeyup, true);
  };

  const deactivateHighlightMode = () => {
    opened.value = true;
    highlightModeSelection.value = null;
    document.removeEventListener('keyup', handleSelectModeKeyup, true);
  };

  const selectEffect = (payload: ConditionalContentSelection) => {
    const effects = EditorConditionsHelper.createEffectFromSelection(payload);
    if (!effects || !updatedConditionalEffect.value) return;

    updatedConditionalEffect.value = {
      ...updatedConditionalEffect.value,
      truthyEffects: [...updatedConditionalEffect.value.truthyEffects, ...effects.truthyEffects],
      falsyEffects: [...updatedConditionalEffect.value.falsyEffects, ...effects.falsyEffects],
    };
    deactivateSelectMode();
  };

  const getConditionalEffectBySelection = (selection: ConditionalContentSelection) =>
    EditorConditionsHelper.findConditionalEffectBySelection(selection, conditionalEffects.value);

  const getEffectsInSection = (selection: ConditionalContentSectionSelection) =>
    EditorConditionsHelper.findConditionalEffectsInEditorSection(
      selection,
      conditionalEffects.value
    );

  const getUpdatedEffectsFromSelection = (selection: ConditionalContentSelection) =>
    EditorConditionsHelper.findEffectInputsBySelection(selection, [
      ...(updatedConditionalEffect.value?.truthyEffects ?? []),
      ...(updatedConditionalEffect.value?.falsyEffects ?? []),
    ]);

  const showConditionOverlay = (editorElement: ConditionalContentSelection) => {
    if (!overlayMode.value) return false;
    if (selectMode.value) return true;

    return (
      highlightMode.value &&
      highlightModeSelection.value &&
      EditorConditionsHelper.isSelectionMatch(editorElement, highlightModeSelection.value)
    );
  };

  const reset = () => {
    deactivateSelectMode();
    conditionalEffects.value = [];
    conditionalEffectPreviews.value.clear();
    opened.value = false;
    selectedConditionalEffectId.value = undefined;
    updatedConditionalEffect.value = undefined;
  };

  const setConditionalEffectPreview = (preview: ConditionalEffectPreview) => {
    conditionalEffectPreviews.value.set(preview.id, preview);
  };
  const setConditionalEffectPreviews = (preview: ConditionalEffectPreview[]) => {
    preview.forEach(preview => conditionalEffectPreviews.value.set(preview.id, preview));
  };
  const getConditionalEffectPreview = (id: string) => conditionalEffectPreviews.value.get(id);
  const hasContentIdReachedLimit = (id: string) => {
    const limitedIds = contentIdsWithLimitReached(
      conditionalEffects.value,
      updatedConditionalEffect.value
    );
    return limitedIds.includes(id);
  };

  return {
    conditionalEffects,
    conditionalEffectPreviews,
    opened,
    selectedConditionalEffectId,
    selectedConditionalEffect,
    listMode,
    editMode,
    createMode,
    openList,
    openEdit,
    openCreate,
    close,
    getConditionalEffectBySelection,
    updatedConditionalEffect,
    selectMode,
    activateSelectMode,
    deactivateSelectMode,
    activateHighlightMode,
    deactivateHighlightMode,
    showConditionOverlay,
    highlightMode,
    highlightModeSelection,
    overlayMode,
    selectEffect,
    reset,
    setConditionalEffectPreview,
    getConditionalEffectPreview,
    setConditionalEffectPreviews,
    getUpdatedEffectsFromSelection,
    unsavedChanges,
    preventCloseReason,
    getEffectsInSection,
    hasContentIdReachedLimit,
  };
});
